import { ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Global UI zoom — scales the whole app via the CSS `zoom` property on <html>
 * (Chromium/WebKit), persisted to localStorage so it survives reloads. Handy on
 * POS touchscreens / shared displays where the browser's own zoom is awkward.
 * Mounted once in the app titlebar, so it applies everywhere (dashboard, POS, …).
 */
const KEY = 'erp-zoom';
const MIN = 0.5;
const MAX = 2;
const STEP = 0.1;

function loadZoom(): number {
  try {
    const v = Number(window.localStorage.getItem(KEY));
    return Number.isFinite(v) && v >= MIN && v <= MAX ? v : 1;
  } catch {
    return 1;
  }
}
const clampZoom = (z: number): number => Math.min(MAX, Math.max(MIN, Math.round(z * 10) / 10));

export function ZoomControls(): JSX.Element {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState<number>(loadZoom);

  useEffect(() => {
    // `zoom` reflows layout (unlike transform:scale) and is honored by Chromium/WebKit.
    const root = document.documentElement;
    (root.style as CSSStyleDeclaration & { zoom?: string }).zoom = String(zoom);
    // The shell's full-height container divides 100vh by this so it still fills the
    // viewport when zoomed out (avoids a blank strip at the bottom).
    root.style.setProperty('--app-zoom', String(zoom));
    try { window.localStorage.setItem(KEY, String(zoom)); } catch { /* ignore */ }
  }, [zoom]);

  return (
    <div className="tb-zoom" role="group" aria-label={t('actions.zoom', { defaultValue: 'Zoom' })} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <button
        type="button"
        className="icon-btn"
        onClick={() => setZoom((z) => clampZoom(z - STEP))}
        disabled={zoom <= MIN}
        aria-label={t('actions.zoomOut', { defaultValue: 'Zoom out' })}
        title={t('actions.zoomOut', { defaultValue: 'Zoom out' })}
      >
        <ZoomOut aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => setZoom(1)}
        aria-label={t('actions.zoomReset', { defaultValue: 'Reset zoom' })}
        title={t('actions.zoomReset', { defaultValue: 'Reset zoom (100%)' })}
        style={{ minWidth: 40, fontSize: 12, fontWeight: 700, color: 'var(--color-fg-muted)', background: 'transparent', border: 0, cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        type="button"
        className="icon-btn"
        onClick={() => setZoom((z) => clampZoom(z + STEP))}
        disabled={zoom >= MAX}
        aria-label={t('actions.zoomIn', { defaultValue: 'Zoom in' })}
        title={t('actions.zoomIn', { defaultValue: 'Zoom in' })}
      >
        <ZoomIn aria-hidden="true" />
      </button>
    </div>
  );
}
